import type { DashboardStats, MovementRecord } from '@jmc/shared';
import { formatCount, formatDate } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n, type MessageKey } from '../lib/i18n.js';
import {
  cx,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Mono,
  PageHeader,
  Panel,
} from '../components/ui.js';

export function DashboardPage() {
  const { t, lang } = useI18n();
  const { user, can } = useAuth();

  const stats = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardStats>('/reports/dashboard'),
  });

  const issued = useQuery({
    queryKey: ['movements', 'open', false],
    queryFn: () => api<{ movements: MovementRecord[] }>('/movements/open'),
  });

  const greeting =
    lang === 'hi'
      ? `नमस्ते, ${user?.fullNameHi ?? user?.fullName ?? ''}`
      : `Welcome, ${user?.fullName ?? ''}`;

  return (
    <>
      <PageHeader
        title={t('nav.dashboard')}
        subtitle={greeting}
        actions={
          <>
            <Link to="/search" className="btn-secondary">
              {t('nav.search')}
            </Link>
            {can('file:import') && (
              <Link to="/import" className="btn-primary">
                {t('nav.import')}
              </Link>
            )}
          </>
        }
      />

      {stats.isLoading && <LoadingBlock />}
      {stats.isError && <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />}

      {stats.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="dashboard.totalFiles" value={stats.data.totalFiles} />
            <StatCard label="dashboard.available" value={stats.data.available} tone="green" />
            <StatCard
              label="dashboard.issued"
              value={stats.data.issued}
              tone="navy"
              to="/issued"
            />
            <StatCard
              label="dashboard.overdue"
              value={stats.data.overdue}
              tone={stats.data.overdue > 0 ? 'amber' : 'plain'}
              to="/issued?overdue=true"
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <Panel
              title={t('dashboard.recentlyIssued')}
              className="lg:col-span-2"
              action={
                <Link to="/issued" className="text-[0.8125rem] text-navy-600 hover:underline">
                  {t('action.viewAll')}
                </Link>
              }
              bodyClassName="overflow-x-auto"
            >
              {issued.isLoading && <LoadingBlock />}
              {issued.data && issued.data.movements.length === 0 && (
                <EmptyState title={t('search.noResults')} />
              )}
              {issued.data && issued.data.movements.length > 0 && (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('field.eFileNumber')}</th>
                      <th>{t('field.issuedTo')}</th>
                      <th>{t('field.expectedReturn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issued.data.movements.slice(0, 8).map((movement) => (
                      <tr key={movement.id}>
                        <td>
                          <Link
                            to={`/files/${movement.fileId}`}
                            className="text-navy-600 hover:underline"
                          >
                            <Mono>{movement.eFileNumber}</Mono>
                          </Link>
                        </td>
                        <td className="truncate">{movement.issuedToName}</td>
                        <td>
                          <span
                            className={cx(
                              movement.isOverdue && 'font-medium text-amber-ink',
                            )}
                          >
                            {formatDate(movement.expectedReturnDate)}
                            {movement.isOverdue && ` · ${t('status.overdue')}`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <div className="space-y-4">
              <Panel title={t('dashboard.byRoom')} bodyClassName="px-4 py-3">
                {stats.data.roomBreakdown.length === 0 ? (
                  <p className="py-4 text-center text-ink-500">{t('common.none')}</p>
                ) : (
                  <BarList
                    items={stats.data.roomBreakdown.map((room) => ({
                      label: `${t('field.room')} ${room.roomCode}`,
                      value: room.count,
                      to: `/search?roomCode=${encodeURIComponent(room.roomCode)}`,
                    }))}
                  />
                )}
              </Panel>

              <Panel title={t('dashboard.topBorrowers')} bodyClassName="px-4 py-3">
                {stats.data.topBorrowers.length === 0 ? (
                  <p className="py-4 text-center text-ink-500">{t('common.none')}</p>
                ) : (
                  <BarList
                    items={stats.data.topBorrowers.map((person) => ({
                      label: person.name,
                      value: person.count,
                    }))}
                  />
                )}
              </Panel>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <SmallStat label="dashboard.addedThisMonth" value={stats.data.filesAddedThisMonth} />
            <SmallStat
              label="dashboard.movementsThisMonth"
              value={stats.data.movementsThisMonth}
            />
            <SmallStat label="dashboard.missing" value={stats.data.missing} />
          </div>
        </>
      )}
    </>
  );
}

const TONE: Record<string, string> = {
  plain: 'text-ink-900',
  green: 'text-green-ink',
  navy: 'text-navy-700',
  amber: 'text-amber-ink',
};

function StatCard({
  label,
  value,
  tone = 'plain',
  to,
}: {
  label: MessageKey;
  value: number;
  tone?: keyof typeof TONE;
  to?: string;
}) {
  const { t } = useI18n();
  const body = (
    <>
      <p className="text-[0.8125rem] text-ink-500">{t(label)}</p>
      <p className={cx('mt-1 text-[1.75rem] font-semibold leading-none tabular', TONE[tone])}>
        {formatCount(value)}
      </p>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="panel block px-4 py-3.5 transition-colors hover:border-navy-300"
      >
        {body}
      </Link>
    );
  }
  return <div className="panel px-4 py-3.5">{body}</div>;
}

function SmallStat({ label, value }: { label: MessageKey; value: number }) {
  const { t } = useI18n();
  return (
    <div className="panel flex items-baseline justify-between px-4 py-3">
      <span className="text-ink-500">{t(label)}</span>
      <span className="text-base font-semibold tabular">{formatCount(value)}</span>
    </div>
  );
}

function BarList({
  items,
}: {
  items: Array<{ label: string; value: number; to?: string }>;
}) {
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const row = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate">{item.label}</span>
              <span className="shrink-0 text-ink-500 tabular">
                {formatCount(item.value)}
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line">
              <div
                className="h-full rounded-full bg-navy-600"
                style={{ width: `${Math.max((item.value / max) * 100, 2)}%` }}
              />
            </div>
          </>
        );
        return (
          <li key={item.label}>
            {item.to ? (
              <Link to={item.to} className="block rounded px-1 py-0.5 hover:bg-navy-50">
                {row}
              </Link>
            ) : (
              <div className="px-1 py-0.5">{row}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
